/**
 * AdminAppointmentsPage — moderation queue for ABMHC appointment requests
 * (and any other hospital-style org_site that uses the appointments block).
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Loader2, CalendarClock, CheckCircle2, XCircle, Phone, Mail, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const SLUG_OPTIONS = [
  { slug: 'abmhc', label: 'ABMHC' },
  { slug: 'bishop-manjoro', label: 'Bishop Manjoro' },
];

const AdminAppointmentsPage = () => {
  const [slug, setSlug] = useState('abmhc');
  const qc = useQueryClient();

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['admin-appointments', slug],
    queryFn: () => api.get(`/sites/admin/${slug}/appointment-requests`).then((r) => r.data),
  });

  const update = useMutation({
    mutationFn: ({ id, ...patch }) => api.post(`/sites/admin/${slug}/appointment-request/${id}/update`, patch),
    onSuccess: () => {
      toast.success('Updated');
      qc.invalidateQueries({ queryKey: ['admin-appointments', slug] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || 'Update failed'),
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/sites/admin/${slug}/appointment-request/${id}`),
    onSuccess: () => {
      toast.success('Deleted');
      qc.invalidateQueries({ queryKey: ['admin-appointments', slug] });
    },
  });

  const buckets = {
    pending: items.filter((p) => p.status === 'pending'),
    scheduled: items.filter((p) => p.status === 'scheduled'),
    done: items.filter((p) => p.status === 'done'),
    declined: items.filter((p) => p.status === 'declined'),
  };

  const card = (a) => (
    <Card key={a.id} className="mb-3" data-testid={`appt-card-${a.id}`}>
      <CardContent className="pt-4 pb-3 space-y-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">{a.name}</span>
            <Badge variant={a.status === 'pending' ? 'destructive' : a.status === 'scheduled' ? 'default' : 'secondary'}>
              {a.status}
            </Badge>
            {a.department && <Badge variant="outline" className="text-xs">{a.department}</Badge>}
            {a.preferred_date && (
              <Badge variant="outline" className="text-xs">
                <CalendarClock className="h-3 w-3 mr-1" /> {a.preferred_date}
              </Badge>
            )}
          </div>
          <p className="text-xs text-slate-500">{a.submitted_at?.slice(0, 16).replace('T', ' ')}</p>
        </div>
        <div className="flex flex-wrap gap-3 text-xs text-slate-600">
          <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {a.phone}</span>
          {a.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" /> {a.email}</span>}
        </div>
        <p className="text-sm text-slate-700 whitespace-pre-line">{a.reason}</p>
        <div className="flex flex-wrap gap-2 pt-2 border-t">
          {a.status !== 'scheduled' && (
            <Button size="sm" onClick={() => update.mutate({ id: a.id, status: 'scheduled' })} data-testid={`appt-schedule-${a.id}`}>
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Schedule
            </Button>
          )}
          {a.status !== 'done' && (
            <Button size="sm" variant="outline" onClick={() => update.mutate({ id: a.id, status: 'done' })} data-testid={`appt-done-${a.id}`}>
              Mark Done
            </Button>
          )}
          {a.status !== 'declined' && (
            <Button size="sm" variant="outline" onClick={() => update.mutate({ id: a.id, status: 'declined' })} data-testid={`appt-decline-${a.id}`}>
              <XCircle className="h-3.5 w-3.5 mr-1" /> Decline
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="text-red-600"
            onClick={() => window.confirm('Delete this appointment request?') && remove.mutate(a.id)}
            data-testid={`appt-delete-${a.id}`}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="admin-appointments-page">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <CalendarClock className="h-5 w-5 text-slate-500" />
          Appointment Requests
        </h1>
        <div className="flex gap-2">
          {SLUG_OPTIONS.map((opt) => (
            <Button
              key={opt.slug}
              size="sm"
              variant={slug === opt.slug ? 'default' : 'outline'}
              onClick={() => setSlug(opt.slug)}
              data-testid={`appt-slug-${opt.slug}`}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="py-20 text-center text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading…
        </div>
      ) : (
        <Tabs defaultValue="pending">
          <TabsList>
            <TabsTrigger value="pending">Pending {buckets.pending.length > 0 && `(${buckets.pending.length})`}</TabsTrigger>
            <TabsTrigger value="scheduled">Scheduled ({buckets.scheduled.length})</TabsTrigger>
            <TabsTrigger value="done">Done ({buckets.done.length})</TabsTrigger>
            <TabsTrigger value="declined">Declined ({buckets.declined.length})</TabsTrigger>
          </TabsList>
          {['pending', 'scheduled', 'done', 'declined'].map((k) => (
            <TabsContent key={k} value={k} className="pt-4">
              {buckets[k].length === 0 ? (
                <Card><CardContent className="py-8 text-center text-sm text-slate-500">No {k} requests.</CardContent></Card>
              ) : buckets[k].map(card)}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
};

export default AdminAppointmentsPage;
